import { useEffect, useState } from 'react'
import { Base, toast } from '@itsrever/design-system'
import { AppstoreintegrationspbIntegrationCard } from '@itsrever/rever-api'
import { useSaveIntegrationCredentials } from '@/application/queries/use-integrations'
import { useEcommerceId } from '@/hooks'

const {
  Button,
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogDescription,
  DialogTitle,
  DialogBackdrop,
  DialogPopup
} = Base

type UninstallDialogProps = {
  integration: Pick<
    AppstoreintegrationspbIntegrationCard,
    'integration_id' | 'integration_name'
  >
}

export const UninstallDialog = ({
  integration: { integration_id, integration_name }
}: UninstallDialogProps) => {
  const [open, setOpen] = useState(false)

  const ecommerceId = useEcommerceId()

  const { mutate, isSuccess, isError, isPending } =
    useSaveIntegrationCredentials()

  const confirmUninstall = () => {
    if (integration_id && integration_name && ecommerceId) {
      mutate(
        {
          integrationId: integration_id,
          integrationName: integration_name,
          ecommerceId,
          credentials: {},
          status: '2'
        },
        {
          onSuccess: () => {
            setOpen(false)
          }
        }
      )
    }
  }

  useEffect(() => {
    if (isSuccess) {
      toast({
        variant: 'success',
        text: 'Integration uninstalled successfully'
      })
    }
  }, [isSuccess])

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        text: 'Error uninstalling integration'
      })
    }
  }, [isError])

  const closeModal = () => {
    setOpen(false)
  }

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <button
            onClick={() => setOpen((prev) => !prev)}
            className="flex h-9 cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-lg border border-solid border-red-500 bg-white px-3 text-sm font-medium text-red-500 shadow-sm transition-all duration-200 ease-in-out  hover:bg-red-500 hover:text-white"
          />
        }
      >
        Uninstall
      </DialogTrigger>
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup className="w-full max-w-md">
          <div className="flex flex-col items-center gap-4 pb-4">
            <div>
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 10C0 4.47715 4.47715 0 10 0H30C35.5228 0 40 4.47715 40 10V30C40 35.5228 35.5228 40 30 40H10C4.47715 40 0 35.5228 0 30V10Z"
                  fill="#FFEBEC"
                />
                <path
                  d="M20 29C15.0293 29 11 24.9707 11 20C11 15.0293 15.0293 11 20 11C24.9707 11 29 15.0293 29 20C29 24.9707 24.9707 29 20 29ZM19.1 22.7V24.5H20.9V22.7H19.1ZM19.1 15.5V20.9H20.9V15.5H19.1Z"
                  fill="#FB3748"
                />
              </svg>
            </div>
            <div className="flex flex-col items-center gap-1">
              <DialogTitle className="text-center !font-sans text-[16px] leading-[24px] text-[#0E121B]">
                Uninstall {integration_name}
              </DialogTitle>
              <DialogDescription className="text-center text-[14px] leading-[20px] text-[#525866]">
                Are you sure you want to uninstall your {integration_name}?
                <p className="text-sm">
                  You can reinstall it on the REVER Marketplace.
                </p>
              </DialogDescription>
            </div>
          </div>
          <div className="flex gap-3 border-t border-solid border-[#E1E4EA] pt-3 *:flex-1">
            <button
              onClick={closeModal}
              className="h-9 flex-1 rounded-lg border border-[#E1E4EA] bg-white p-2 text-sm font-medium text-[#525866] shadow-[0px_1px_2px_rgba(10,13,20,0.03)]"
            >
              Cancel
            </button>
            <Button
              onClick={confirmUninstall}
              loading={isPending}
              variant="primary"
              className=" bg-[#FB3748] hover:!bg-red-500"
            >
              Uninstall
            </Button>
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}
